/**
 * Todo API Client
 *
 * All calls include Authorization: Bearer <token> from authStore.
 * Returns typed responses matching @signa/shared types.
 */

import type { Todo, TodoProject, TodoSection } from '@signa/shared';
import { TODO_API } from './api-config';
import { useAuthStore } from '../stores/authStore';

// Auth header helper

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as { message?: string }).message ?? `HTTP ${res.status}`;
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

// Response types (API surface)

export interface TodoListResponse {
  todos: ApiTodo[];
  projects: ApiProject[];
  sections: ApiSection[];
}

/** Serialized todo from API (camelCase snake_case converted by Hono) */
export interface ApiTodo {
  id: string;
  projectId: string;
  sectionId?: string;
  title: string;
  descriptionText?: string;
  priority: 1 | 2 | 3 | 4;
  isCompleted: boolean;
  completedAt?: string;
  dueDate?: string;
  labels: string[];
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiProject {
  id: string;
  name: string;
  color: string;
  icon: string;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiSection {
  id: string;
  projectId: string;
  name: string;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}

// Mappers: API to Store/UI types

export function apiTodoToTodo(t: ApiTodo): Todo {
  return {
    id: t.id,
    userId: '', // not needed on client
    title: t.title,
    description: t.descriptionText,
    priority: t.priority,
    dueDate: t.dueDate,
    completedAt: t.completedAt,
    isRecurring: false,
    labels: t.labels,
    position: t.orderIndex,
    projectId: t.projectId,
    sectionId: t.sectionId,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
  };
}

export function apiProjectToProject(p: ApiProject): TodoProject {
  return {
    id: p.id,
    userId: '',
    name: p.name,
    color: p.color,
    position: p.orderIndex,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
}

export function apiSectionToSection(s: ApiSection): TodoSection {
  return {
    id: s.id,
    projectId: s.projectId,
    name: s.name,
    position: s.orderIndex,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  };
}

// Todo API

export const todoApi = {
  list(params?: {
    projectId?: string;
    sectionId?: string;
    completed?: boolean;
  }): Promise<TodoListResponse> {
    const url = new URL(TODO_API.list());
    if (params?.projectId) url.searchParams.set('projectId', params.projectId);
    if (params?.sectionId) url.searchParams.set('sectionId', params.sectionId);
    if (params?.completed !== undefined) url.searchParams.set('completed', String(params.completed));
    return apiFetch<TodoListResponse>(url.toString());
  },

  // NLP mode: send raw input string, server parses it
  createNLP(input: string): Promise<{ todo: ApiTodo }> {
    return apiFetch<{ todo: ApiTodo }>(TODO_API.create(), {
      method: 'POST',
      body: JSON.stringify({ input }),
    });
  },

  // Explicit mode
  createExplicit(data: {
    title: string;
    projectId: string;
    sectionId?: string;
    descriptionText?: string;
    priority?: 1 | 2 | 3 | 4;
    dueDate?: string;
    labels?: string[];
  }): Promise<{ todo: ApiTodo }> {
    return apiFetch<{ todo: ApiTodo }>(TODO_API.create(), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: string, data: Partial<Todo>): Promise<{ todo: ApiTodo }> {
    // Remap camelCase Todo fields to what the API expects
    const body: Record<string, unknown> = {};
    if (data.title !== undefined) body.title = data.title;
    if (data.description !== undefined) body.descriptionText = data.description;
    if (data.priority !== undefined) body.priority = data.priority;
    if (data.dueDate !== undefined) body.dueDate = data.dueDate;
    if (data.projectId !== undefined) body.projectId = data.projectId;
    if (data.sectionId !== undefined) body.sectionId = data.sectionId;
    if (data.labels !== undefined) body.labels = data.labels;
    if (data.completedAt !== undefined) body.completedAt = data.completedAt;
    if (data.position !== undefined) body.orderIndex = data.position;
    return apiFetch<{ todo: ApiTodo }>(TODO_API.update(id), {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  delete(id: string): Promise<void> {
    return apiFetch<void>(TODO_API.delete(id), { method: 'DELETE' });
  },

  toggleComplete(id: string): Promise<{ todo: ApiTodo }> {
    return apiFetch<{ todo: ApiTodo }>(TODO_API.toggleComplete(id), {
      method: 'POST',
    });
  },

  reorder(projectId: string, orderedIds: string[]): Promise<void> {
    return apiFetch<void>(TODO_API.reorder(), {
      method: 'POST',
      body: JSON.stringify({ projectId, orderedIds }),
    });
  },

  bulkComplete(ids: string[], completed: boolean): Promise<void> {
    return apiFetch<void>(TODO_API.bulkComplete(), {
      method: 'POST',
      body: JSON.stringify({ ids, completed }),
    });
  },

  // Projects

  createProject(data: {
    name: string;
    color?: string;
    icon?: string;
  }): Promise<{ project: ApiProject }> {
    return apiFetch<{ project: ApiProject }>(TODO_API.createProject(), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateProject(
    id: string,
    data: Partial<TodoProject>,
  ): Promise<{ project: ApiProject }> {
    const body: Record<string, string | number> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.color !== undefined) body.color = data.color;
    if (data.position !== undefined) body.orderIndex = data.position;
    return apiFetch<{ project: ApiProject }>(TODO_API.updateProject(id), {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  deleteProject(id: string): Promise<void> {
    return apiFetch<void>(TODO_API.deleteProject(id), { method: 'DELETE' });
  },

  // Sections

  createSection(data: {
    name: string;
    projectId: string;
  }): Promise<{ section: ApiSection }> {
    return apiFetch<{ section: ApiSection }>(TODO_API.createSection(), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSection(
    id: string,
    data: Partial<TodoSection>,
  ): Promise<{ section: ApiSection }> {
    const body: Record<string, string | number> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.position !== undefined) body.orderIndex = data.position;
    if (data.projectId !== undefined) body.projectId = data.projectId;
    return apiFetch<{ section: ApiSection }>(TODO_API.updateSection(id), {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  deleteSection(id: string): Promise<void> {
    return apiFetch<void>(TODO_API.deleteSection(id), { method: 'DELETE' });
  },
};
